import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck, faSquare, faCircleUp, faCircleDown, } from '@fortawesome/free-regular-svg-icons';
import { faShareNodes } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '../../components/UI/Modal';
import { Code } from '../../components/UI/Code';
import { Loading } from '../../components/UI/Loading';
import { CrosswordForm } from '../../components/Crossword/CrosswordForm';
import { WordItem } from '../../components/Crossword/WordItem';
import { WordBox } from '../../components/Crossword/WordBox';
import { Grid } from '../../components/Crossword/Grid';
import { getCrossword, updateCrossword, updateCrosswordStatus, updateWords, deleteCrossword, } from '../../redux/actions/crosswords';
import { clearError } from '../../redux/reducers/crosswords';
import { generateGrid } from '../../utils/crossword';
import { configService } from '../../services/config.service';
const { playServer } = await configService.appConfig;
const EditCrosswordComponent = ({ crosswordDetail, getCrossword, updateCrossword, updateCrosswordStatus, updateWords, deleteCrossword, clearError, loading, error, errorDetail, }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isUpdatingWords, setIsUpdatingWords] = useState(false);
    const [generatedGrid, setGeneratedGrid] = useState(null);
    const [copied, setCopied] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();
    const params = useParams();
    const crosswordId = params.id;
    /**
     * Effect to fetch crossword details if not already loaded
     * Cleans up error state on unmount
     */
    useEffect(() => {
        // Clear any previous errors and fetch crossword details
        if (crosswordId) {
            clearError({});
            getCrossword(crosswordId);
        }
    }, [crosswordId, getCrossword, clearError]);
    useEffect(() => {
        if (crosswordDetail?.seed) {
            const gridData = generateGrid(crosswordDetail.seed);
            setGeneratedGrid({
                cells: gridData.newGrid,
                length: gridData.length,
                height: gridData.height,
            });
        }
        else {
            setGeneratedGrid(null);
        }
    }, [crosswordDetail?.seed, crosswordDetail?.settings.randomiseGrid]);
    // Guard placed after all hooks so hook order stays stable across renders
    if (!crosswordId)
        return React.createElement("div", null, "Crossword not found");
    const handleCodeCopy = () => {
        setCopied(true);
    };
    const handleModalOpen = (event) => {
        event.preventDefault();
        setIsModalOpen(true);
        setCopied(false);
    };
    const handleModalClose = () => {
        setIsModalOpen(false);
    };
    const handleStatusUpdate = (event) => {
        event.preventDefault();
        if (crosswordDetail) {
            updateCrosswordStatus({
                crosswordId,
                status: crosswordDetail.status === 'draft' ? 'published' : 'draft',
            });
        }
    };
    /**
     * Additional check to ensure we have the correct crossword loaded
     * If the loaded crossword doesn't match the current crosswordId, show loading
     */
    const isCorrectCrosswordLoaded = crosswordDetail && crosswordDetail.crosswordId === crosswordId;
    const handleCrosswordUpdate = (crosswordData) => {
        if (crosswordDetail) {
            updateCrossword({
                crosswordId: crosswordDetail.crosswordId,
                data: {
                    name: crosswordData.name,
                    duration: crosswordData.duration,
                    settings: crosswordData.settings,
                },
            });
        }
        setIsEditing(false);
    };
    /**
     * Handles edit cancellation
     * Exits edit mode without saving changes
     */
    const handleEditCancel = () => {
        setIsEditing(false);
    };
    /**
     * Handles crossword deletion
     * Deletes the crossword and navigates back to list
     */
    const handleCrosswordDelete = () => {
        if (crosswordDetail) {
            deleteCrossword(crosswordDetail.crosswordId);
            navigate('/crosswords');
        }
    };
    /**
     * Handles words update
     * @param {Array<Word>} words - Updated words array
     */
    const handleUpdateWords = (words, seed) => {
        if (crosswordDetail) {
            updateWords({ crosswordId: crosswordDetail.crosswordId, words, seed });
            if (seed) {
                const gridData = generateGrid(seed);
                setGeneratedGrid({
                    cells: gridData.newGrid,
                    length: gridData.length,
                    height: gridData.height,
                });
            }
        }
        setIsUpdatingWords(false);
    };
    /**
     * Handles word edit cancellation
     * Exits word edit mode without saving changes
     */
    const handleEditWordsCancel = () => {
        setIsUpdatingWords(false);
    };
    // Show loading while fetching data
    if (loading)
        return React.createElement(Loading, null);
    // Show error message if there's an API error
    if (error) {
        return (React.createElement("div", { className: "error-container" },
            React.createElement("h3", null, "Error loading crossword"),
            React.createElement("p", null, errorDetail || 'Failed to load crossword details. Please try again.'),
            React.createElement("button", { className: "btn--primary", onClick: () => {
                    clearError({});
                    getCrossword(crosswordId);
                } }, "Retry")));
    }
    // Show error if crossword not found after successful API call
    if (!loading && !crosswordDetail) {
        return (React.createElement("div", { className: "error-container" },
            React.createElement("h3", null, "Crossword not found"),
            React.createElement("p", null, "The crossword you're looking for doesn't exist or has been deleted."),
            React.createElement("button", { className: "btn--primary", onClick: () => window.history.back() }, "Go Back")));
    }
    // Show loading if we don't have the correct crossword loaded yet
    if (!isCorrectCrosswordLoaded)
        return React.createElement(Loading, null);
    return (React.createElement(React.Fragment, null,
        crosswordDetail && crosswordDetail.code && (React.createElement(Modal, { id: crosswordDetail.code, component: Code, componentProps: {
                id: crosswordDetail.code,
                link: `${playServer}?code=${crosswordDetail.code}`,
                copied,
                onCopy: handleCodeCopy,
            }, isOpen: isModalOpen, onClose: handleModalClose })),
        crosswordDetail && (React.createElement("div", { className: "activity-container" },
            isEditing ? (React.createElement(CrosswordForm, { crossword: crosswordDetail, onSubmit: handleCrosswordUpdate, onCancel: handleEditCancel })) : (React.createElement("div", { className: "activity-item-no-hover" },
                React.createElement("div", { className: "activity-header" },
                    React.createElement("h2", null, crosswordDetail.name)),
                React.createElement("div", { className: "activity-settings" },
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: crosswordDetail.settings.timed === true
                                ? faSquareCheck
                                : faSquare }),
                        "Timed"),
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: crosswordDetail.settings.randomiseGrid === true
                                ? faSquareCheck
                                : faSquare }),
                        "Randomise Grid"),
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: crosswordDetail.settings.sendResultEmail === true
                                ? faSquareCheck
                                : faSquare }),
                        "Send Result Email"),
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: crosswordDetail.settings.public === true
                                ? faSquareCheck
                                : faSquare }),
                        "Share Publicly")),
                crosswordDetail.duration && (React.createElement("div", { className: "activity-meta" },
                    React.createElement("span", null,
                        "Duration: ",
                        parseInt(crosswordDetail.duration) / 60,
                        " min",
                        ' '))),
                React.createElement("div", { className: "activity-actions" },
                    React.createElement("button", { className: "btn--primary", onClick: () => {
                            setIsEditing(true);
                        }, disabled: crosswordDetail.status === 'published' }, "Edit"),
                    React.createElement("button", { className: "btn--primary publish", onClick: handleStatusUpdate, disabled: crosswordDetail.words.length === 0 },
                        React.createElement(FontAwesomeIcon, { icon: crosswordDetail.status === 'draft'
                                ? faCircleUp
                                : faCircleDown }),
                        crosswordDetail.status === 'draft' ? 'Publish' : 'Unpublish'),
                    React.createElement("button", { className: "btn--secondary share", value: crosswordDetail.code, onClick: handleModalOpen, disabled: crosswordDetail.status === 'draft' },
                        React.createElement(FontAwesomeIcon, { icon: faShareNodes }),
                        "Host"),
                    React.createElement("button", { className: "btn--delete", onClick: handleCrosswordDelete, disabled: crosswordDetail.status === 'published' }, "Delete")))),
            crosswordDetail.words && crosswordDetail.words.length > 0 ? (isUpdatingWords ? (React.createElement(WordBox, { randomiseGrid: crosswordDetail.settings.randomiseGrid, words: crosswordDetail.words, seed: crosswordDetail.seed, onUpdate: handleUpdateWords, onCancel: handleEditWordsCancel })) : (React.createElement("div", { className: "word-item__container" },
                React.createElement("div", { className: "word-item__content" },
                    React.createElement("div", { className: "word-item__words" },
                        React.createElement("div", { className: "word-item__row" },
                            React.createElement("div", { className: "word-item__answer--heading" },
                                React.createElement("h4", null, "ANSWER")),
                            React.createElement("div", { className: "word-item__clue--heading" },
                                React.createElement("h4", null, "CLUE"))),
                        crosswordDetail.words.map((word, index) => (React.createElement(WordItem, { key: index, word: word })))),
                    !crosswordDetail.settings.randomiseGrid && generatedGrid && (React.createElement("div", { className: "word-form__grid-container" },
                        React.createElement(Grid, { cells: generatedGrid.cells, height: generatedGrid.height, length: generatedGrid.length })))),
                React.createElement("div", { className: "word-item__controls" },
                    React.createElement("button", { className: "btn--primary", onClick: () => setIsUpdatingWords(true), disabled: crosswordDetail.status === 'published' }, "Edit Words"))))) : isUpdatingWords ? (React.createElement(React.Fragment, null,
                React.createElement(WordBox, { randomiseGrid: crosswordDetail.settings.randomiseGrid, words: crosswordDetail.words, seed: !crosswordDetail.settings.randomiseGrid
                        ? crosswordDetail.seed
                        : undefined, onUpdate: handleUpdateWords, onCancel: handleEditWordsCancel }))) : (React.createElement("button", { className: "btn--primary", onClick: () => setIsUpdatingWords(true) }, "Add Words"))))));
};
const mapStateToProps = (state) => {
    return {
        crosswords: state.crosswordState.crosswords,
        crosswordDetail: state.crosswordState.crosswordDetail,
        loading: state.crosswordState.loading,
        error: state.crosswordState.error,
        errorDetail: state.crosswordState.errorDetail,
    };
};
const mapDispatchToProps = {
    getCrossword,
    updateCrossword,
    updateCrosswordStatus,
    updateWords,
    deleteCrossword,
    clearError,
};
export const EditCrossword = connect(mapStateToProps, mapDispatchToProps)(EditCrosswordComponent);
